/* eslint-disable react-hooks/exhaustive-deps */
import constate from 'constate'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useSnackbarContext } from './useSnackBar'
import { SubscriptionData, SubscriptionProvider } from './useTierSelection'
import { useZippieIdContext } from './useZippieId'
import apiClient from '../api/apiClient'
import { base64ToFile, extractBase64Images } from '../utils/richTextEditorUtils'

export interface IFile {
  id: string | null
  url?: string
}

interface IPrice {
  interval: string
  currency: string
  amount: string
  amount_formatted: string
}

interface Subscription {
  plan: string
  data: SubscriptionData
  provider: SubscriptionProvider
  isEligibleForTrial: null | boolean
  features: Feature[]
  price?: IPrice
  status: string
}

interface Feature {
  scope: string
  name: string
  count?: {
    min: number
    max: number
    current: number
  }
}

export interface IWorkspace {
  _salesRepId?: string
  id?: string
  avatar?: IFile
  description?: string
  website?: string
  facebook?: string
  twitter?: string
  youtube?: string
  instagram?: string
  linkedin?: string
  name?: string
  country?: string
  phoneNumber?: string
  operator?: string
  status?: 'active' | 'deactivated'
  subscription?: Subscription
  userId?: string
  _workspaceId?: string
  workspaceRoles?: { name: string }[]
  type?: string
  createdAt?: Date
}

interface ISalesRepresentatives {
  _id: string
  _userId: string
  name: string
  email: string
}

const WORKSPACE_ID = 'selectedWorkspaceId'

export const useWorkspace = () => {
  const { workspaceId } = useParams()
  const location = useLocation()
  const navigation = useNavigate()

  const { isLoggedIn } = useZippieIdContext()
  const { openSnackBar } = useSnackbarContext()

  const currentPlanDetailsIsLoadedRef = useRef<boolean>(false)
  const customerProfileIsLoadedRef = useRef<boolean>(false)
  const subscriptionPlansIsLoadedRef = useRef<boolean>(false)
  const billingHistoryIsLoadedRef = useRef<boolean>(false)

  const [workspaces, setWorkspaces] = useState<IWorkspace[]>([])
  const [salesRepresentatives, setSalesRepresentatives] = useState<ISalesRepresentatives[]>([])
  const [isCreateWorkspaceModal, setIsCreateWorkspaceModal] = useState(false)
  const [isWorkspacePrivate, setIsWorkspacePrivate] = useState<boolean>(false)
  const [isCreateAdditionalWorkspaceModal, setIsCreateAdditionalWorkspaceModal] = useState<boolean>(false)
  const [selectedWorkspaceDetails, setSelectedWorkspaceDetails] = useState<IWorkspace>({})
  const [isWorkspaceFetchingInProgress, setIsWorkspaceFetchingInProgress] = useState<boolean>(true)

  const [selectedNodeId, setSelectedNodeId] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(WORKSPACE_ID)
      return saved ? JSON.parse(saved) : ''
    } catch (error) {
      console.error('Error reading from localStorage:', error)
      return ''
    }
  })

  const onWorkspaceChange = async (workspaceId: string) => {
    setSelectedNodeId(workspaceId)
    navigation(`/${workspaceId}`)
    currentPlanDetailsIsLoadedRef.current = false
    customerProfileIsLoadedRef.current = false
    subscriptionPlansIsLoadedRef.current = false
    billingHistoryIsLoadedRef.current = false
  }

  const getWorkspaceDetails = async (nodeId: string) => {
    const { data } = await apiClient.get(`/workspace/${nodeId}`)
    const details = data.data
    return {
      ...data,
      _salesRepId: details._salesRepId,
      id: details._id,
      status: details.status,
      name: details.name,
      description: details.description,
      avatar: details.picture,
      subscription: details.subscription,
      userId: details._userId,
      type: details.type,
      ...details.social,
    }
  }

  const updateWorkspace = async (workspaceDetails: IWorkspace) => {
    const payload = {
      _pictureId: workspaceDetails.avatar?.id,
      description: workspaceDetails.description,
      name: workspaceDetails.name,
      social: {
        facebook: workspaceDetails.facebook,
        instagram: workspaceDetails.instagram,
        linkedin: workspaceDetails.linkedin,
        twitter: workspaceDetails.twitter,
        website: workspaceDetails.website,
        youtube: workspaceDetails.youtube,
      },
    }
    await apiClient.patch(`/workspace/${workspaceDetails.id}`, payload)
    await updateSelectedWorkspace()
  }

  const fetchWorkspaces = async () => {
    const { data } = await apiClient.get(`/workspaces`)
    const workspaces = data.data.map((node: any) => ({
      id: node._id,
      status: node.status,
      name: node.name,
      description: node.description,
      country: node.country,
      pictureId: node._pictureId,
      social: node.social,
      subscription: node.subscription,
      avatar: node.picture,
      type: node.type,
      createdAt: node.createdAt,
    }))

    setWorkspaces(workspaces)
    return workspaces
  }

  const getUserInvitesToWorkspace = async ({
    sortBy = 'createdAt,-1',
    skip = 0,
    limit = 50,
    status = [],
    query = '',
  }: {
    sortBy?: string
    skip?: number
    limit?: number
    status?: string[]
    query?: string
  }) => {
    const { data } = await apiClient.get<any, any>(`/workspace_invites`, {
      params: {
        sortBy,
        skip,
        limit,
        status: status || undefined,
        query,
      },
    })
    return data
  }

  const fetchSalesRepresentatives = async () => {
    const { data } = await apiClient.get<any, any>(`/sales_representatives`)
    setSalesRepresentatives(data.data)
  }

  const getAllWorkspacesAdmin = async ({
    sortBy = 'createdAt,-1',
    skip = 0,
    limit = 50,
    status = ['active', 'archived'],
    query = '',
  }: {
    sortBy?: string
    skip?: number
    limit?: number
    status?: string[]
    query?: string
  }) => {
    const { data } = await apiClient.get<any, any>(`/workspaces?admin=yes`, {
      params: {
        sortBy,
        skip,
        limit,
        status: status || undefined,
        query,
      },
    })
    const workspaces = data.data.map((node: any) => ({
      id: node._id,
      status: node.status,
      name: node.name,
      description: node.description,
      country: node.country,
      pictureId: node._pictureId,
      social: node.social,
      subscription: node.subscription,
      avatar: node.picture,
      type: node.type,
      ...node,
    }))
    return { count: data.count, workspaces }
  }

  const addWorkspace = async (workspaceDetails: { name: string }) => {
    const inviteCode = localStorage.getItem('invite_owner')
    const { data } = await apiClient.post<any, any>(`/workspace/`, {
      name: workspaceDetails.name,
      inviteCode: inviteCode || undefined,
    })
    await fetchWorkspaces()
    onWorkspaceChange(data.data._id)
    localStorage.removeItem('invite_owner')
    return data.data
  }

  async function richTextEditorUpload(value: string): Promise<string> {
    const base64Images: string[] = extractBase64Images(value)

    const uploadedImageUrls = await Promise.all(
      base64Images.map(async (base64Image) => {
        const imageFile = base64ToFile(base64Image, 'image.png') // Convert base64 to File object
        const imageUrl = await uploadFile(imageFile, selectedNodeId) // Pass the File object to uploadFile
        const response = await apiClient.post(`/workspace/${workspaceId}/file/${imageUrl.id}/verify`)

        return response.data.data.url
      }),
    )

    let updatedContent: string = value
    for (let i = 0; i < base64Images.length; i++) {
      updatedContent = updatedContent.replace(base64Images[i], uploadedImageUrls[i])
    }

    return updatedContent
  }

  const uploadFile = async (
    file: File,
    selectedNodeId: string | number,
    fileName: string = file.name.split('.')[0],
    altText: string = file.name,
  ): Promise<any> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('alt', file.name.replace(/\s+/g, '_'))
    formData.append('visibility', 'public')
    formData.append('encrypted', 'false')
    formData.append('workspaceId', String(selectedNodeId))

    try {
      const response = await apiClient.put(`/workspace/${selectedNodeId}/file`, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        workspaceId: selectedNodeId,
        name: fileName,
        alt: altText,
      })
      await apiClient.put(response.data.data.url, file, { withCredentials: false })

      return {
        id: response.data.data._id,
        url: response.data.data.url,
      }
    } catch (error) {
      console.error(error)
    }
  }

  const multipleUpload = async (files: File[], selectedNodeId: string | number) => {
    const uploadFiles = await Promise.all(files.map((file) => uploadFile(file, selectedNodeId)))
    return uploadFiles
  }

  const deleteWorkspace = async (workspaceId: string | number) => {
    await apiClient.delete(`/workspace/${workspaceId}`)
    const workspaces = await fetchWorkspaces()
    if (workspaces.length !== 0) {
      onWorkspaceChange(workspaces[0].id)
    } else {
      localStorage.clear()
      navigation('/')
      window.location.reload()
    }
  }

  const getTemplateById = async (templateId: string) => {
    const { data } = await apiClient.get(`/workspace/${selectedNodeId}/templates`, {
      params: {
        templateId,
      },
    })
    const { file } = data.data[0]
    return await apiClient.get(file.url, {
      withCredentials: false,
    })
  }

  const updateSelectedWorkspace = async () => {
    setIsWorkspaceFetchingInProgress(true)
    const workspaceDetails = await getWorkspaceDetails(selectedNodeId)

    const isPrivate = workspaceDetails.type === 'private'
    setIsWorkspacePrivate(isPrivate)
    setSelectedWorkspaceDetails(workspaceDetails)

    setIsWorkspaceFetchingInProgress(false)
  }

  const getOngoingTransferInvites = async () => {
    const response = await apiClient.get<any, any>(`/workspace/${workspaceId}/ownership/transfer`)
    return response.data
  }

  const onStart = async () => {
    const workspaces = await fetchWorkspaces()

    if (!workspaces.length && !location.pathname.includes('transfer_ownership')) {
      setIsCreateWorkspaceModal(true)
      return
    }
    const inviteCode = localStorage.getItem('invite_owner')
    if (inviteCode) {
      setIsCreateAdditionalWorkspaceModal(true)
    }

    if (
      !workspaceId &&
      !location.pathname.includes('admin') &&
      !location.pathname.includes('invites') &&
      !location.pathname.includes('transfer_ownership')
    ) {
      onWorkspaceChange(String(workspaces[0].id))
      return
    }
    if (workspaceId) setSelectedNodeId(workspaceId)
  }

  const transferWorkspaceOwnership = async (formData: {
    userId?: string
    email?: string
    name?: string
    signature?: {
      rs?: string
      recovery?: number
    }
    assignor: {
      roles?: null | Array<{ _id: string; _projectId: Array<object>; scopes: Array<object> }>
    }
  }) => {
    try {
      const { data } = await apiClient.post<any, any>(`/workspace/${workspaceId}/ownership/transfer`, {
        assignee: {
          email: formData.email,
          name: formData.name,
        },
        assignor: {
          roles: formData.assignor.roles
            ? formData.assignor.roles.map((role) => ({
                _id: role._id, // (Admin role ID)
                _projectId: role._projectId || [], // "_projectId": []
                scopes: role.scopes || [], // "scopes": []
              }))
            : [],
        },
        signature: {
          rs: formData.signature?.rs,
          recovery: formData.signature?.recovery,
        },
      })
      openSnackBar({
        type: 'success',
        message: 'Workspace ownership invite has been sent successfully!',
      })
      return data
    } catch (err) {
      openSnackBar({
        type: 'error',
        message: `Error`,
      })
      console.error(err)
    }
  }

  useEffect(() => {
    if (!isLoggedIn) return
    onStart()
  }, [isLoggedIn])

  useEffect(() => {
    if (selectedNodeId) {
      updateSelectedWorkspace()
    }
  }, [selectedNodeId])

  useEffect(() => {
    if (selectedNodeId) {
      try {
        localStorage.setItem(WORKSPACE_ID, JSON.stringify(selectedNodeId))
      } catch (error) {
        console.error('Error saving to localStorage:', error)
      }
    }
  }, [selectedNodeId])

  useEffect(() => {
    if (workspaceId && workspaceId !== selectedNodeId) {
      setSelectedNodeId(workspaceId)
    }
  }, [workspaceId, selectedNodeId])

  return {
    workspaces,
    addWorkspace,
    updateWorkspace,
    getWorkspaceDetails,
    selectedNodeId,
    uploadFile,
    deleteWorkspace,
    selectedWorkspaceDetails,
    setSelectedWorkspaceDetails,
    multipleUpload,
    updateSelectedWorkspace,
    isWorkspaceFetchingInProgress,
    richTextEditorUpload,
    isCreateWorkspaceModal,
    setIsCreateWorkspaceModal,
    isCreateAdditionalWorkspaceModal,
    setIsCreateAdditionalWorkspaceModal,
    onWorkspaceChange,
    isWorkspacePrivate,
    getAllWorkspacesAdmin,
    getUserInvitesToWorkspace,
    getTemplateById,
    salesRepresentatives,
    fetchSalesRepresentatives,
    transferWorkspaceOwnership,
    getOngoingTransferInvites,
    currentPlanDetailsIsLoadedRef,
    customerProfileIsLoadedRef,
    subscriptionPlansIsLoadedRef,
    billingHistoryIsLoadedRef,
  }
}

const [WorkspaceContainer, useWorkspaceContext] = constate(useWorkspace)

export { useWorkspaceContext, WorkspaceContainer }
